import { useState, useEffect } from "react";
import React from 'react';
import moment from 'moment';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Modal,
  message,
  Table,
  Typography,
  Card,
  Alert,
  Statistic,
  Space,
  Tag,
  theme,
  Select,
  DatePicker,
  Dropdown,
  Tooltip
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  StockOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SearchOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  TeamOutlined,
  AppstoreOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useDebounce } from "use-debounce";
import { saveAs } from 'file-saver';
import { utils, writeFile } from 'xlsx';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { useToken } = theme;

const LOW_STOCK_THRESHOLD = 50;
const OVERSTOCK_THRESHOLD = 200;

const StockSummaryCard = ({ title, value, status, icon, trend }) => {
  const { token } = useToken();
  const statusColors = {
    low: token.colorError,
    over: token.colorWarning,
    normal: token.colorSuccess,
  };

  const trendIcons = {
    up: <ArrowUpOutlined style={{ color: token.colorSuccess }} />,
    down: <ArrowDownOutlined style={{ color: token.colorError }} />,
  };

  return (
    <Card
      bordered={false}
      style={{
        background: `linear-gradient(135deg, ${statusColors[status]}20, transparent)`,
        borderRadius: token.borderRadiusLG,
      }}
    >
      <Space size="middle" align="start">
        <div
          style={{
            padding: 8,
            background: `${statusColors[status]}10`,
            borderRadius: token.borderRadiusSM,
          }}
        >
          {React.cloneElement(icon, {
            style: { fontSize: 24, color: statusColors[status] },
          })}
        </div>
        <div>
          <Text type="secondary">{title}</Text>
          <Statistic
            value={value}
            valueStyle={{
              fontSize: 24,
              fontWeight: 600,
              color: statusColors[status],
            }}
          />
          {trend && <Text type="secondary">{trendIcons[trend]} 12% from last month</Text>}
        </div>
      </Space>
    </Card>
  );
};

const RawMaterialReceivingNote = () => {
  const [form] = Form.useForm();
  const { token } = useToken();
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [stock, setStock] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [reportData, setReportData] = useState([]);
  const [reportParams, setReportParams] = useState({
    type: 'monthly',
    year: moment().year(),
    month: moment().month() + 1,
    quarter: 1,
    customRange: [moment().startOf('month'), moment().endOf('month')]
  });

  const filteredData = dataSource.filter(note =>
    Object.values(note).some(value =>
      String(value).toLowerCase().includes(debouncedSearch.toLowerCase())
    )
  );

  const calculateStockMetrics = () => {
    const stockEntries = Object.entries(stock);
    return {
      totalItems: stockEntries.length,
      totalStock: stockEntries.reduce((sum, [_, quantity]) => sum + quantity, 0),
      lowStockItems: stockEntries.filter(([_, quantity]) => quantity <= LOW_STOCK_THRESHOLD).length,
      overstockItems: stockEntries.filter(([_, quantity]) => quantity >= OVERSTOCK_THRESHOLD).length,
    };
  };

  const { totalItems, totalStock, lowStockItems, overstockItems } = calculateStockMetrics();

  const fetchData = async () => {
    try {
      const response = await axios.get("https://agpipefitting.org.et/api/receiving-notes");
      setDataSource(response.data);
      
      const initialStock = {};
      response.data.forEach(note => {
        note.items.forEach(item => {
          initialStock[item.item_code] = (initialStock[item.item_code] || 0) + Number(item.quantity);
        });
      });
      setStock(initialStock);
    } catch (error) {
      message.error("Failed to load data");
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleGenerateReport = () => {
    try {
      message.loading({ content: 'Generating report...', key: 'report', duration: 2 });
      
      const filtered = dataSource.filter(note => {
        const noteDate = moment(note.grn_date);
        switch(reportParams.type) {
          case 'monthly':
            return noteDate.year() === reportParams.year && 
                   noteDate.month() + 1 === reportParams.month;
          case 'quarterly':
            const quarterStartMonth = (reportParams.quarter - 1) * 3;
            const quarterEndMonth = quarterStartMonth + 2;
            return noteDate.year() === reportParams.year && 
                  noteDate.month() >= quarterStartMonth && 
                  noteDate.month() <= quarterEndMonth;
          case 'yearly':
            return noteDate.year() === reportParams.year;
          case 'custom':
            if (!reportParams.customRange?.[0] || !reportParams.customRange?.[1]) return false;
            return noteDate.isBetween(reportParams.customRange[0], reportParams.customRange[1], null, '[]');
          default:
            return false;
        }
      });

      const processedData = filtered.map(note => ({
        ...note,
        total_qty: note.items.reduce((sum, item) => sum + Number(item.quantity), 0),
        total_value: note.items.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unit_price)), 0)
      }));

      setReportData(processedData);
      message.success({ content: 'Report generated successfully', key: 'report', duration: 2 });
    } catch (error) {
      message.error({ content: 'Report generation failed. Check parameters.', key: 'report', duration: 3 });
      console.error('Report error:', error);
    }
  };

  const handleExportCSV = () => {
    if (reportData.length === 0) {
      message.warning('No data to export');
      return;
    }

    const csvContent = [
      ['GRN Number', 'Department', 'Total Quantity', 'Total Value (ETB)', 'Items'],
      ...reportData.map(record => [
        `"${record.grn_number}"`,
        `"${record.requested_department}"`,
        record.total_qty,
        record.total_value.toFixed(2),
        `"${record.items.map(item => `${item.item_name} (${item.quantity})`).join('; ')}"`
      ])
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `material_report_${moment().format('YYYYMMDD_HHmmss')}.csv`);
  };

  const handleExportExcel = () => {
    if (reportData.length === 0) {
      message.warning('No data to export');
      return;
    }

    const worksheet = utils.json_to_sheet(reportData.map(record => ({
      'GRN Number': record.grn_number,
      'Department': record.requested_department,
      'Total Quantity': record.total_qty,
      'Total Value (ETB)': record.total_value,
      'Items': record.items.map(item => `${item.item_name} (${item.quantity})`).join(', ')
    })));

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Material Report');
    writeFile(workbook, `material_report_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  const handleOpenModal = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingNote(null);
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      ...record,
      grn_date: moment(record.grn_date),
      items: record.items.map(item => ({
        ...item,
        quantity: item.quantity.toString(),
        unit_price: item.unit_price.toString(),
       
      })),
    });
    setEditingNote(record);
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: "Are you sure you want to delete this receiving note and all its items?",
      okText: "Delete",
      okType: "danger",
      centered: true,
      onOk: async () => {
        try {
          message.loading({ content: 'Deleting...', key: 'deleteMsg', duration: 0 });
          
          await axios.delete(`https://agpipefitting.org.et/api/receiving-notes/${record.id}`);
          
          message.success({
            content: 'Receiving note deleted successfully!',
            key: 'deleteMsg',
            duration: 2
          });
          
          await fetchData();
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Failed to connect to server';
          message.error({
            content: `Deletion failed: ${errorMessage}`,
            key: 'deleteMsg',
            duration: 3
          });
        }
      },
    });
  };

  const onFinish = async (values) => {
    try {
        setLoading(true);
        const processedValues = {
            ...values,
            grn_date: values.grn_date.format('YYYY-MM-DD'),
            items: values.items.map(item => ({
                ...item,
                quantity: parseFloat(item.quantity).toFixed(2),  // Ensures 2 decimal places
                unit_price: parseFloat(item.unit_price).toFixed(2),
               
            })),
        };

        if (editingNote) {
            await axios.put(
                `https://agpipefitting.org.et/api/receiving-notes/${editingNote.grn_number}`,
                processedValues
            );
        } else {
            await axios.post(
                "https://agpipefitting.org.et/api/receiving-notes",
                processedValues
            );
        }

        await fetchData();
        message.success(`Note ${editingNote ? "updated" : "created"} successfully`);
        handleCloseModal();
    } catch (error) {
        message.error(error.response?.data?.message || "Operation failed");
    } finally {
        setLoading(false);
    }
};


  const columns = [
    {
      title: "GRN Number",
      dataIndex: "grn_number",
      key: "grn_number",
      fixed: "left",
      sorter: (a, b) => a.grn_number.localeCompare(b.grn_number),
    },
    {
      title: "Department",
      dataIndex: "requested_department",
      key: "requested_department",
      sorter: (a, b) => a.requested_department.localeCompare(b.requested_department),
    },
    {
      title: "PR Number",
      dataIndex: "purchase_requisition_number",
      key: "purchase_requisition_number",
    },
    {
      title: "GRN Date",
      dataIndex: "grn_date",
      key: "grn_date",
      render: (date) => moment(date).format('YYYY-MM-DD'),
      sorter: (a, b) => moment(a.grn_date).unix() - moment(b.grn_date).unix(),
    },
    {
      title: "Total Qty",
      dataIndex: "items",
      key: "total_quantity",
      render: (items) => items.reduce((sum, item) => sum + Number(item.quantity), 0).toLocaleString(),
      sorter: (a, b) => 
        a.items.reduce((sum, item) => sum + Number(item.quantity), 0) -
        b.items.reduce((sum, item) => sum + Number(item.quantity), 0),
    },
    {
      title: "Total Cost",
      dataIndex: "items",
      key: "total_cost",
      render: (items) => 
        `ETB ${items
          .reduce((sum, item) => sum + parseFloat(item.quantity) * parseFloat(item.unit_price), 0)
          .toFixed(2)}`,  // Ensures 2 decimal places
      sorter: (a, b) => {
        const totalA = a.items.reduce((sum, item) => sum + parseFloat(item.quantity) * parseFloat(item.unit_price), 0);
        const totalB = b.items.reduce((sum, item) => sum + parseFloat(item.quantity) * parseFloat(item.unit_price), 0);
        return totalA - totalB;
      },
    },
    
    {
      title: "Stock Status",
      dataIndex: "items",
      key: "stock",
      render: (items) => (
        <Space size={[4, 8]} wrap>
          {items.map((item) => {
            const currentStock = stock[item.item_code] || 0;
            const isLow = currentStock <= LOW_STOCK_THRESHOLD;
            const isOver = currentStock >= OVERSTOCK_THRESHOLD;
            
            return (
              <Tag
                key={item.item_code}
                icon={isLow ? <StockOutlined /> : null}
                color={isLow ? "error" : isOver ? "warning" : "success"}
                style={{ margin: 0 }}
              >
                <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>{item.item_name}</span>: 
                <span 
                  style={{ 
                    color: isLow ? 'red' : isOver ? 'orange' : 'green', 
                    fontWeight: 'bold' 
                  }}
                >
                  {currentStock}
                </span>
              </Tag>
            );
          })}
        </Space>
      ),
    },
    
    
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            shape="circle"
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            shape="circle"
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1366, margin: "0 auto" }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: 24,
        borderBottom: `1px solid ${token.colorBorder}`,
        paddingBottom: 16
      }}>
        <Title level={3} style={{ margin: 0 }}>
          📦 Raw Material Inventory
        </Title>
        <Input
          placeholder="Search notes..."
          prefix={<SearchOutlined />}
          style={{ width: 300 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StockSummaryCard
            title="Total Items"
            value={totalItems}
            status={totalItems === 0 ? "low" : "normal"}
            icon={<StockOutlined />}
            trend="up"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StockSummaryCard
            title="Total Stock"
            value={totalStock}
            status={totalStock === 0 ? "low" : "normal"}
            icon={<StockOutlined />}
            trend="down"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StockSummaryCard
            title="Low Stock"
            value={lowStockItems}
            status={lowStockItems > 0 ? "low" : "normal"}
            icon={<StockOutlined />}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StockSummaryCard
            title="Overstock"
            value={overstockItems}
            status={overstockItems > 0 ? "over" : "normal"}
            icon={<StockOutlined />}
          />
        </Col>
      </Row>

      <Card 
        className="mb-4 shadow-sm" 
        title={
          <Space>
            <FilePdfOutlined />
            <span>Advanced Report Generator</span>
          </Space>
        }
        extra={
          <Space>
            <Text type="secondary">Last Generated:</Text>
            <Text>{moment().format('YYYY-MM-DD HH:mm')}</Text>
          </Space>
        }
      >
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Select
              value={reportParams.type}
              onChange={type => setReportParams({ ...reportParams, type })}
              style={{ width: '100%' }}
            >
              <Option value="monthly">Monthly</Option>
              <Option value="quarterly">Quarterly</Option>
              <Option value="yearly">Yearly</Option>
              <Option value="custom">Custom Range</Option>
            </Select>
          </Col>

          {reportParams.type === 'yearly' && (
            <Col span={4}>
              <DatePicker
                picker="year"
                value={moment().year(reportParams.year)}
                onChange={date => setReportParams({ ...reportParams, year: date.year() })}
              />
            </Col>
          )}

          {reportParams.type === 'quarterly' && (
            <>
              <Col span={4}>
                <DatePicker
                  picker="year"
                  value={moment().year(reportParams.year)}
                  onChange={date => setReportParams({ ...reportParams, year: date.year() })}
                />
              </Col>
              <Col span={4}>
                <Select
                  value={reportParams.quarter}
                  onChange={quarter => setReportParams({ ...reportParams, quarter })}
                  style={{ width: '100%' }}
                >
                  <Option value={1}>Q1</Option>
                  <Option value={2}>Q2</Option>
                  <Option value={3}>Q3</Option>
                  <Option value={4}>Q4</Option>
                </Select>
              </Col>
            </>
          )}

          {reportParams.type === 'monthly' && (
            <Col span={6}>
              <DatePicker
                picker="month"
                value={moment().year(reportParams.year).month(reportParams.month - 1)}
                onChange={date => setReportParams({ 
                  ...reportParams, 
                  year: date.year(),
                  month: date.month() + 1
                })}
              />
            </Col>
          )}

          {reportParams.type === 'custom' && (
            <Col span={8}>
              <RangePicker
                value={reportParams.customRange}
                onChange={dates => setReportParams({ ...reportParams, customRange: dates })}
              />
            </Col>
          )}

          <Col>
            <Button 
              type="primary" 
              onClick={handleGenerateReport}
              icon={<FilePdfOutlined />}
              style={{ minWidth: 180 }}
            >
              Generate Smart Report
            </Button>
          </Col>

          {reportData.length > 0 && (
            <Col>
              <Dropdown.Button
                menu={{
                  items: [
                    { key: 'csv', label: 'CSV Format', onClick: handleExportCSV },
                    { key: 'excel', label: 'Excel Format', onClick: handleExportExcel },
                  ],
                }}
                icon={<FileExcelOutlined />}
              >
                Export Dataset
              </Dropdown.Button>
            </Col>
          )}
        </Row>

        {reportData.length > 0 && (
          <div className="mt-4">
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Total Departments"
                  value={new Set(reportData.map(r => r.requested_department)).size}
                  prefix={<TeamOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Average Value/GRN"
                  value={reportData.reduce((sum, r) => sum + r.total_value, 0) / reportData.length}
                  precision={2}
                  prefix="ETB"
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Unique Items"
                  value={new Set(reportData.flatMap(r => r.items.map(i => i.item_code))).size}
                  prefix={<AppstoreOutlined />}
                />
              </Col>
            </Row>

            <Table
              columns={[
                { 
                  title: 'GRN Number', 
                  dataIndex: 'grn_number', 
                  key: 'grn_number',
                  sorter: (a, b) => a.grn_number.localeCompare(b.grn_number),
                },
                { 
                  title: 'Department', 
                  dataIndex: 'requested_department', 
                  key: 'department',
                  filters: [
                    { text: 'Production', value: 'Production' },
                    { text: 'Logistics', value: 'Logistics' },
                    { text: 'Quality Control', value: 'Quality Control' },
                  ],
                  onFilter: (value, record) => record.requested_department.includes(value),
                },
                {
                  title: "Total Quantity",
                  dataIndex: "total_qty",
                  key: "total_qty",
                  sorter: (a, b) => parseFloat(a.total_qty) - parseFloat(b.total_qty), // Ensures decimal-safe sorting
                  render: (value) => parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), // Formats with 2 decimal places
                },
                
                {
                  title: "Total Value (ETB)",
                  dataIndex: "total_value",
                  key: "total_value",
                  sorter: (a, b) => parseFloat(a.total_value) - parseFloat(b.total_value), // Ensures decimal-safe sorting
                  render: (value) =>
                    parseFloat(value).toLocaleString("en-ET", {
                      style: "currency",
                      currency: "ETB",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }), // Ensures two decimal places
                },
                
                {
                  title: 'Items',
                  dataIndex: 'items',
                  key: 'items',
                  render: (items) => (
                    <Tooltip 
                      title={items.map(item => `${item.item_name} (${item.quantity})`).join(', ')}
                    >
                      {items.length} items
                    </Tooltip>
                  ),
                },
              ]}
              dataSource={reportData}
              rowKey="grn_number"
              className="mt-4"
              pagination={{ 
                pageSize: 10, 
                showTotal: (total) => `Showing ${total} professional entries`,
                showSizeChanger: true 
              }}
              scroll={{ x: true }}
              bordered
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Text strong>Grand Total</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <Text strong>
                        {reportData.reduce((sum, r) => sum + r.total_qty, 0).toLocaleString()}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      <Text strong>
                        {reportData.reduce((sum, r) => sum + r.total_value, 0)
                          .toLocaleString('en-ET', { 
                            style: 'currency', 
                            currency: 'ETB',
                            minimumFractionDigits: 2
                          })}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </div>
        )}
      </Card>

      {lowStockItems > 0 && (
        <Alert
          message={`Low Stock Alert: ${lowStockItems} items below minimum threshold (${LOW_STOCK_THRESHOLD})`}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16, borderRadius: token.borderRadiusLG }}
        />
      )}

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleOpenModal}
        style={{ marginBottom: 16, borderRadius: token.borderRadiusLG }}
        size="large"
      >
        Add New Entry
      </Button>

      <Table
        bordered
        dataSource={filteredData}
        columns={columns}
        rowKey="grn_number"
        pagination={{ 
          pageSize: 5, 
          showSizeChanger: false,
          style: { marginRight: 16 }
        }}
        scroll={{ x: true }}
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowTertiary,
        }}
        rowClassName={() => "hover-row"}
        expandable={{
          expandedRowRender: (record) => {
            const itemColumns = [
              { 
                title: 'Item Code', 
                dataIndex: 'item_code', 
                key: 'item_code' 
              },
              { 
                title: 'Item Name', 
                dataIndex: 'item_name', 
                key: 'item_name' 
              },
              {
                title: 'Quantity',
                dataIndex: 'quantity',
                key: 'quantity',
                render: (value) =>
                  parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), // Handles decimals correctly
              },
              
              
              {
                title: 'Unit Price',
                dataIndex: 'unit_price',
                key: 'unit_price',
                render: (value) => `ETB ${parseFloat(value).toFixed(2)}`, // Handles decimals correctly
              },
              
              {
                title: 'Total',
                key: 'total',
                render: (_, item) => `ETB ${(parseFloat(item.quantity) * parseFloat(item.unit_price)).toFixed(2)}`
              },
              
            ];

            return (
              <Table
                columns={itemColumns}
                dataSource={record.items}
                pagination={false}
                bordered
                size="small"
                rowKey="item_code"
              />
            );
          },
          rowExpandable: (record) => record.items.length > 0,
        }}
      />

      <Modal
        title={editingNote ? "Edit Receiving Note" : "Create New Receiving Note"}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={1000}
        centered
        styles={{
          body: { paddingTop: 24 },
          header: { borderBottom: `1px solid ${token.colorBorder}` }
        }}
      >
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="GRN Number"
                name="grn_number"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input 
                  placeholder="GRN-001" 
                  disabled={!!editingNote}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Requested Department"
                name="requested_department"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input 
                  placeholder="Production Department" 
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Purchase Requisition Number"
                name="purchase_requisition_number"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input 
                  placeholder="PR-2023-001" 
                  size="large"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="GRN Date"
                name="grn_date"
                rules={[{ required: true, message: "Required" }]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  size="large" 
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Items Received</Text>
                </div>
                
                {fields.map(({ key, name, ...restField }) => (
                  <Row 
                    key={key} 
                    gutter={8} 
                    align="middle" 
                    style={{ marginBottom: 16 }}
                  >
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "item_code"]}
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Input placeholder="Item Code" size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        {...restField}
                        name={[name, "item_name"]}
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <Input placeholder="Item Name" size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
  <Form.Item
    {...restField}
    name={[name, "quantity"]}
    rules={[{ required: true, message: "Required" }]}
  >
    <Input 
      type="number" 
      step="0.01" 
      placeholder="Quantity" 
      min={1} 
      size="large"
    />
  </Form.Item>
</Col>

<Col span={4}>
  <Form.Item
    {...restField}
    name={[name, "unit_price"]}
    rules={[{ required: true, message: "Required" }]}
  >
    <Input 
      type="number" 
      placeholder="Unit Price" 
      step="0.01" 
      size="large"
      prefix="ETB "
    />
  </Form.Item>
</Col>

                    <Col span={2}>
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        size="large"
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  size="large"
                  style={{ borderRadius: token.borderRadiusLG }}
                >
                  Add Item
                </Button>
              </>
            )}
          </Form.List>

          <Form.Item style={{ marginTop: 32 }}>
            <Space style={{ float: 'right' }}>
              <Button 
                onClick={handleCloseModal}
                style={{ 
                  paddingInline: 24,
                  borderRadius: token.borderRadiusLG 
                }}
                size="large"
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                style={{ 
                  paddingInline: 24,
                  borderRadius: token.borderRadiusLG 
                }}
                size="large"
              >
                {editingNote ? "Update Note" : "Create Note"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RawMaterialReceivingNote;